'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { ABTesting } from '../config/abTestingConfig/abTestingCounter';
import { getFeatureFlags } from '../config/features';
import { setCookie } from '../utils/cookies';

import { AB_TESTS } from '@/shared/config/abTestingConfig';

export type Variant = 'a' | 'b' | 'default';

const USER_ID_COOKIE = 'ab_test_user_id';

const generateUserId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);

  return `${timestamp}_${random}`;
};

const getCookie = (name: string): string | null => {
  try {
    if (typeof document === 'undefined') {
      return null;
    }

    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));

    return match ? match[2] : null;
  } catch (error) {
    console.warn('Failed to read cookie:', error);

    return null;
  }
};

/**
 * Хук для A/B тестирования с поддержкой SSR
 *
 * @param testId - ID теста (например, 'main_button_test_05_2026')
 * @returns 'a' | 'b' | 'default'
 *   - 'a' или 'b' - активный A/B тест, показываем один из вариантов
 *   - 'default' - тест выключен, показываем дефолтное поведение (оба варианта)
 *
 * @example
 * ```tsx
 * const variant = useAbTest('main_button_test_05_2026');
 *
 * const buttonConfig = {
 *   default: { mainButton: true, secondaryButton: true },
 *   a: { mainButton: true },
 *   b: { secondaryButton: true },
 * }[variant];
 * ```
 */
export const useAbTest = (
  testId: string,
): { variant: Variant; isLoading: boolean } => {
  const [variant, setVariant] = useState<Variant>('a');
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();
  const { enableAbTestingPages } = getFeatureFlags();

  const abTestingInstance = useMemo(() => new ABTesting(testId), [testId]);

  useEffect(() => {
    if (!enableAbTestingPages) {
      /**
       * в случае, если тесты выключены глобально
       * нам нужно показывать ОБА варианта
       * (дефолтное поведение)
       */
      setVariant('default');
      setIsLoading(false);

      return;
    }

    const testConfig = AB_TESTS[testId];

    if (!testConfig?.enabled) {
      /**
       * в случае, если тест выключен
       * нам нужно показывать ОБА варианта
       */
      setVariant('default');
      setIsLoading(false);

      return;
    }

    // Проверяем, активен ли тест на текущей странице
    const isPageActive = testConfig.pages.some(
      (page) => pathname === page || pathname.startsWith(`${page}/`),
    );

    if (!isPageActive) {
      /**
       * в случае, если тест выключен на текущей странице
       * нам нужно показывать ОБА варианта
       */
      setVariant('default');
      setIsLoading(false);

      return;
    }

    const variantCookie = `ab_test_${testId}`;

    // Получаем или создаём userId
    let userId = getCookie(USER_ID_COOKIE);

    if (!userId) {
      userId = generateUserId();

      setCookie(USER_ID_COOKIE, userId);
    }

    // Получаем или определяем вариант
    let savedVariant = getCookie(variantCookie) as Variant | null;

    if (!savedVariant || (savedVariant !== 'a' && savedVariant !== 'b')) {
      savedVariant = abTestingInstance.getVariant();
    }

    setVariant(savedVariant);
    setIsLoading(false);
  }, [enableAbTestingPages, testId, pathname, abTestingInstance]);

  return { variant, isLoading };
};
